import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeft, CalendarDays, Clock, FileSignature, FileText,
  History, MessageCircle, Pencil, Phone, Plus, Trash2, UserCheck, UserX,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import { useCatalogos } from '@/components/candidatos/catalogos';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ActivityTimeline from '@/components/owners/ActivityTimeline';
import BloqueTareas from '@/components/tareas/BloqueTareas';
import CampoOculto, { BotonSensibles } from '@/components/personal/CampoOculto';
import DocumentosEmpleado from '@/components/personal/DocumentosEmpleado';
import EmpleadoForm from '@/components/personal/EmpleadoForm';
import ModalAusencia from '@/components/personal/ModalAusencia';
import ModalContrato from '@/components/personal/ModalContrato';
import ModalSituacion from '@/components/personal/ModalSituacion';
import OperativaCard from '@/components/personal/OperativaCard';
import SituacionBadge from '@/components/personal/SituacionBadge';
import type { Activity } from '@/lib/types';
import {
  desplazarMes, ETIQUETA_LABEL, fechaCorta, JORNADA_LABEL, mesActual, MOTIVO_BAJA_LABEL,
  NIVEL_IDIOMA_LABEL, nombreDelMes, TERNARIO_LABEL, TIPO_AUSENCIA_LABEL, TIPO_CONTRATO_LABEL,
  TIPO_DOCUMENTO_IDENTIDAD_LABEL,
  type Ausencia, type Contrato, type DatosSensibles, type FichaEmpleado, type ResumenFichajes,
} from '@/lib/personal';

type Pestana = 'datos' | 'contrato' | 'documentos' | 'ausencias' | 'fichajes' | 'actividad';

const PESTANAS: { clave: Pestana; etiqueta: string; icono: any }[] = [
  { clave: 'datos', etiqueta: 'Datos', icono: FileText },
  { clave: 'contrato', etiqueta: 'Contrato', icono: FileSignature },
  { clave: 'documentos', etiqueta: 'Documentos', icono: FileText },
  { clave: 'ausencias', etiqueta: 'Ausencias', icono: CalendarDays },
  { clave: 'fichajes', etiqueta: 'Fichajes', icono: Clock },
  { clave: 'actividad', etiqueta: 'Actividad', icono: History },
];

/**
 * La ficha del empleado.
 *
 * Vive fuera del layout de Personal a propósito: en un móvil, la cabecera del
 * layout más sus pestañas más las de aquí se comen media pantalla antes del
 * primer dato. Mismo motivo que el detalle de un alojamiento en Reseñas.
 *
 * Los datos sensibles (DNI, Seguridad Social, IBAN, salarios) NO llegan con la
 * ficha: se piden aparte al pulsar «Mostrar», y esa lectura queda registrada en
 * la actividad. Por eso el botón está donde se ve y no escondido.
 */
export default function EmpleadoDetallePage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { catalogos } = useCatalogos();
  const [params, setParams] = useSearchParams();

  const pestana = (params.get('tab') as Pestana) || 'datos';
  const cambiarPestana = (p: Pestana) => {
    const siguiente = new URLSearchParams(params);
    siguiente.set('tab', p);
    setParams(siguiente, { replace: true });
  };

  const [ficha, setFicha] = useState<FichaEmpleado | null>(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState<string | null>(null);

  const [editando, setEditando] = useState(false);
  const [modalSituacion, setModalSituacion] = useState(false);
  const [modalContrato, setModalContrato] = useState<{ contrato: Contrato | null } | null>(null);
  const [modalAusencia, setModalAusencia] = useState<{ ausencia: Ausencia | null } | null>(null);

  const [sensibles, setSensibles] = useState<DatosSensibles | null>(null);
  const [cargandoSensibles, setCargandoSensibles] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setFicha(await api.getEmpleado(id));
      setFallo(null);
    } catch (err) {
      setFallo(err instanceof Error ? err.message : 'No se ha podido cargar la ficha');
    } finally {
      setCargando(false);
    }
  }, [id]);

  useEffect(() => { void cargar(); }, [cargar]);

  const { data: actividades, refresh: refrescarActividades } = useApi<Activity[]>(
    () => api.getActivities({ entityId: id, entityType: 'empleado' }),
    [id],
  );

  const alternarSensibles = async () => {
    if (sensibles) return setSensibles(null);
    setCargandoSensibles(true);
    try {
      setSensibles(await api.getDatosSensibles(id));
      refrescarActividades();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'No se han podido cargar');
    } finally {
      setCargandoSensibles(false);
    }
  };

  /** Tras cualquier escritura: la ficha vuelve entera, y la actividad cambia. */
  const trasGuardar = (empleado: FichaEmpleado['empleado']) => {
    setFicha((f) => (f ? { ...f, empleado } : f));
    refrescarActividades();
    // Vacaciones, operativa y avisos los recalcula el servidor: se relee.
    void cargar();
  };

  if (cargando && !ficha) return <LoadingSpinner />;
  if (fallo || !ficha) {
    return (
      <div className="card">
        <p className="text-sm text-red-600">{fallo || 'Ficha no encontrada'}</p>
        <Link to="/equipo/personal" className="btn-secondary mt-4 inline-block">Volver a la plantilla</Link>
      </div>
    );
  }

  const { empleado: e, operativa, vacaciones, avisoSinContrato } = ficha;

  return (
    <div>
      <Link to="/equipo/personal" className="inline-flex items-center gap-1.5 text-sm text-sereno-dark/50 hover:text-sereno-dark mb-4">
        <ArrowLeft size={16} /> Plantilla
      </Link>

      {/* Cabecera */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 rounded-full bg-sereno-dark text-white flex items-center justify-center text-sm font-medium flex-shrink-0">
            {e.nombreCompleto.split(/\s+/).slice(0, 2).map((p) => p[0] || '').join('').toUpperCase() || '?'}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold truncate">{e.nombreCompleto}</h1>
              <SituacionBadge situacion={e.situacion} />
            </div>
            <p className="text-sm text-sereno-dark/50">
              {e.puesto ? catalogos.puestoLabel[e.puesto] || e.puesto : 'Sin puesto'}
              {e.zona ? ` · ${catalogos.zonaLabel[e.zona] || e.zona}` : ''}
              {e.fechaAlta ? ` · desde ${fechaCorta(e.fechaAlta)}` : ''}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {e.telefono && (
            <>
              <a href={`tel:${e.telefono}`} className="btn-secondary p-2" title={e.telefono}><Phone size={16} /></a>
              {e.telefonoNormalizado && (
                <a
                  href={`https://wa.me/${e.telefonoNormalizado}`}
                  target="_blank" rel="noopener noreferrer"
                  className="btn-secondary p-2" title="WhatsApp"
                >
                  <MessageCircle size={16} />
                </a>
              )}
            </>
          )}
          <button onClick={() => setModalSituacion(true)} className="btn-secondary flex items-center gap-1.5 text-sm">
            {e.situacion === 'alta' ? <UserX size={16} /> : <UserCheck size={16} />}
            <span className="hidden sm:inline">{e.situacion === 'alta' ? 'Dar de baja' : 'Dar de alta'}</span>
          </button>
          <button onClick={() => setEditando(true)} className="btn-primary flex items-center gap-1.5 text-sm">
            <Pencil size={16} /> <span className="hidden sm:inline">Editar</span>
          </button>
        </div>
      </div>

      {/* Avisos que no pueden esperar a una pestaña */}
      {avisoSinContrato && (
        <Aviso tono="amber">
          Está de alta y no tiene contrato en vigor. Añádelo en la pestaña Contrato.
        </Aviso>
      )}
      {e.lavanderiaDesincronizada && (
        <Aviso tono="red">
          La app de lavandería no se ha podido actualizar con la última alta o baja. Se reintenta solo cada día.
        </Aviso>
      )}
      {e.caducado && (
        <Aviso tono="red">
          Esta ficha ha cumplido su plazo de conservación ({fechaCorta(e.conservarHasta)}). Decide si se borra.
        </Aviso>
      )}

      {/* Pestañas */}
      <div className="border-b border-slate-200 dark:border-white/10 mb-6">
        <nav className="flex gap-1 -mb-px overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-none">
          {PESTANAS.map(({ clave, etiqueta, icono: Icono }) => (
            <button
              key={clave}
              onClick={() => cambiarPestana(clave)}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-3 sm:py-2.5 text-sm whitespace-nowrap border-b-2 transition-colors ${
                pestana === clave
                  ? 'border-sereno-dark text-sereno-dark font-medium dark:border-white dark:text-white'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <Icono size={15} />
              {etiqueta}
              {clave === 'documentos' && e.documentos.length > 0 && (
                <span className="text-xs text-sereno-dark/40">{e.documentos.length}</span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {pestana === 'datos' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Identidad</h3>
                <BotonSensibles visible={!!sensibles} cargando={cargandoSensibles} onAlternar={() => void alternarSensibles()} />
              </div>
              <Datos>
                <Dato etiqueta={e.documentoIdentidad ? TIPO_DOCUMENTO_IDENTIDAD_LABEL[e.documentoIdentidad.tipo] : 'Documento'}>
                  <CampoOculto
                    valor={sensibles?.documentoIdentidad?.numero ?? null}
                    tiene={!!e.documentoIdentidad}
                    cargando={cargandoSensibles}
                    onMostrar={alternarSensibles}
                    copiable
                  />
                  {e.documentoIdentidad?.caduca && (
                    <span className="text-xs text-sereno-dark/40"> · caduca {fechaCorta(e.documentoIdentidad.caduca)}</span>
                  )}
                </Dato>
                <Dato etiqueta="Nº Seguridad Social">
                  <CampoOculto
                    valor={sensibles?.numeroSeguridadSocial ?? null}
                    tiene={e.tieneNumeroSeguridadSocial}
                    cargando={cargandoSensibles}
                    onMostrar={alternarSensibles}
                    copiable
                  />
                </Dato>
                <Dato etiqueta="Fecha de nacimiento">{fechaCorta(e.fechaNacimiento)}</Dato>
                <Dato etiqueta="Nacionalidad">{e.nacionalidad || '—'}</Dato>
                <Dato etiqueta="IBAN">
                  <CampoOculto
                    valor={sensibles?.iban ?? null}
                    tiene={e.tieneIban}
                    cargando={cargandoSensibles}
                    onMostrar={alternarSensibles}
                    copiable
                  />
                </Dato>
              </Datos>
            </div>

            <div className="card">
              <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-3">Contacto</h3>
              <Datos>
                <Dato etiqueta="Teléfono">{e.telefono || '—'}</Dato>
                <Dato etiqueta="Otro teléfono">{e.telefonoSecundario || '—'}</Dato>
                <Dato etiqueta="Email">{e.email || '—'}</Dato>
                <Dato etiqueta="Dirección">
                  {[e.direccion?.calle, e.direccion?.cp, e.direccion?.localidad, e.direccion?.provincia]
                    .filter(Boolean).join(', ') || '—'}
                </Dato>
                <Dato etiqueta="En caso de emergencia">
                  {e.contactoEmergencia?.nombre
                    ? `${e.contactoEmergencia.nombre}${e.contactoEmergencia.relacion ? ` (${e.contactoEmergencia.relacion})` : ''}${e.contactoEmergencia.telefono ? ` · ${e.contactoEmergencia.telefono}` : ''}`
                    : '—'}
                </Dato>
                <Dato etiqueta="Carnet / vehículo">
                  {TERNARIO_LABEL[e.carnetConducir]} · {TERNARIO_LABEL[e.vehiculoPropio]}
                </Dato>
                <Dato etiqueta="Idiomas">
                  {e.idiomas.length
                    ? e.idiomas.map((i) => `${i.codigo.toUpperCase()} (${NIVEL_IDIOMA_LABEL[i.nivel]})`).join(', ')
                    : '—'}
                </Dato>
              </Datos>
            </div>

            {e.notas && (
              <div className="card">
                <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-2">Notas</h3>
                <p className="text-sm whitespace-pre-wrap">{e.notas}</p>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <OperativaCard
              empleado={e}
              operativa={operativa}
              onEnlazar={async () => {
                try {
                  const { empleado } = await api.enlazarConLavanderia(e.id);
                  trasGuardar(empleado);
                } catch (err) {
                  alert(err instanceof Error ? err.message : 'No se ha podido enlazar');
                }
              }}
            />

            <div className="card">
              <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-3">
                Vacaciones {vacaciones.anio}
              </h3>
              <p className="text-3xl font-bold tabular-nums">
                {vacaciones.restantes}
                <span className="text-sm font-normal text-sereno-dark/40"> de {vacaciones.correspondientes} días</span>
              </p>
              <p className="text-xs text-sereno-dark/50 mt-1">
                {vacaciones.consumidos} consumidos
                {vacaciones.prorrateado && ` · prorrateado por ${vacaciones.diasTrabajadosEnElAnio} días de alta`}
              </p>
              {vacaciones.restantes < 0 && (
                <p className="text-xs text-red-600 mt-1">Ha cogido {Math.abs(vacaciones.restantes)} días de más.</p>
              )}
              <p className="text-[11px] text-sereno-dark/35 mt-2">
                Orientativo, en días naturales. El saldo bueno es el de la gestoría.
              </p>
            </div>

            {e.etiquetas.length > 0 && (
              <div className="card">
                <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-2">Etiquetas</h3>
                <div className="flex flex-wrap gap-1.5">
                  {e.etiquetas.map((t) => (
                    <span key={t} className="badge bg-gray-100 text-sereno-dark/60">{ETIQUETA_LABEL[t] || t}</span>
                  ))}
                </div>
              </div>
            )}

            {e.candidatoId && (
              <Link
                to={`/equipo/candidatos/${e.candidatoId}`}
                className="card block hover:bg-sereno-blue/10 transition-colors"
              >
                <p className="text-sm font-medium">Vino de la bolsa de empleo</p>
                <p className="text-xs text-sereno-dark/50 mt-0.5">Ver su ficha de candidato</p>
              </Link>
            )}
          </div>
        </div>
      )}

      {pestana === 'contrato' && (
        <PestanaContrato
          ficha={ficha}
          sensibles={sensibles}
          onNuevo={() => setModalContrato({ contrato: null })}
          onEditar={(c) => setModalContrato({ contrato: c })}
          onBorrar={async (c) => {
            if (!confirm('¿Eliminar este contrato del historial?')) return;
            try {
              const { empleado } = await api.borrarContratoEmpleado(e.id, c.id);
              trasGuardar(empleado);
            } catch (err) {
              alert(err instanceof Error ? err.message : 'No se ha podido borrar');
            }
          }}
        />
      )}

      {pestana === 'documentos' && (
        <DocumentosEmpleado empleado={e} onActualizado={trasGuardar} />
      )}

      {pestana === 'ausencias' && (
        <PestanaAusencias
          ficha={ficha}
          onNueva={() => setModalAusencia({ ausencia: null })}
          onEditar={(a) => setModalAusencia({ ausencia: a })}
        />
      )}

      {pestana === 'fichajes' && <PestanaFichajes empleadoId={e.id} />}

      {pestana === 'actividad' && (
        <div className="space-y-6">
          <BloqueTareas relatedType="empleado" relatedId={e.id} relatedName={e.nombreCompleto} />
          <div className="card">
            <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-3">Actividad</h3>
            <ActivityTimeline activities={actividades || []} />
          </div>
          {(e.caducado || (!e.fechaAlta && e.contratos.length === 0)) && (
            <div className="card border border-red-200 bg-red-50/50">
              <h3 className="text-sm font-semibold text-red-800">Borrar la ficha definitivamente</h3>
              <p className="text-xs text-red-700/80 mt-1 mb-3">
                Se irán sus documentos y su actividad. No se toca ni la app de lavandería ni sus fichajes,
                que no son del CRM. No se puede deshacer.
              </p>
              <button
                onClick={async () => {
                  if (!confirm(`¿Borrar definitivamente la ficha de ${e.nombreCompleto}?`)) return;
                  try {
                    await api.borrarEmpleado(e.id);
                    navigate('/equipo/personal');
                  } catch (err) {
                    alert(err instanceof Error ? err.message : 'No se ha podido borrar');
                  }
                }}
                className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:underline"
              >
                <Trash2 size={15} /> Borrar la ficha
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modales */}
      <Modal isOpen={editando} onClose={() => setEditando(false)} title="Editar ficha" size="xl">
        <EmpleadoForm
          empleado={e}
          onCancelar={() => setEditando(false)}
          onGuardar={async (datos) => {
            const { empleado } = await api.actualizarEmpleado(e.id, datos);
            setEditando(false);
            setSensibles(null);
            trasGuardar(empleado);
          }}
        />
      </Modal>

      {modalSituacion && (
        <ModalSituacion
          abierto
          empleado={e}
          onCerrar={() => setModalSituacion(false)}
          onGuardar={async (datos) => {
            const { empleado, aviso } = await api.cambiarSituacion(e.id, datos);
            trasGuardar(empleado);
            if (aviso) alert(aviso);
          }}
        />
      )}

      {modalContrato && (
        <ModalContrato
          abierto
          contrato={modalContrato.contrato}
          documentos={e.documentos}
          onCerrar={() => setModalContrato(null)}
          onGuardar={async (datos) => {
            const { empleado } = modalContrato.contrato
              ? await api.actualizarContratoEmpleado(e.id, modalContrato.contrato.id, datos)
              : await api.crearContratoEmpleado(e.id, datos);
            trasGuardar(empleado);
          }}
        />
      )}

      {modalAusencia && (
        <ModalAusencia
          abierto
          ausencia={modalAusencia.ausencia}
          documentos={e.documentos}
          onCerrar={() => setModalAusencia(null)}
          onGuardar={async (datos) => {
            const { empleado } = modalAusencia.ausencia
              ? await api.actualizarAusencia(e.id, modalAusencia.ausencia.id, datos)
              : await api.crearAusencia(e.id, datos);
            trasGuardar(empleado);
          }}
          onBorrar={
            modalAusencia.ausencia
              ? async () => {
                  const { empleado } = await api.borrarAusencia(e.id, modalAusencia.ausencia!.id);
                  trasGuardar(empleado);
                }
              : undefined
          }
        />
      )}
    </div>
  );
}

// ─── Piezas ───────────────────────────────────────────────────────────────

function Aviso({ tono, children }: { tono: 'amber' | 'red'; children: React.ReactNode }) {
  const clases = tono === 'amber' ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-red-50 border-red-200 text-red-800';
  return (
    <div className={`rounded-lg border p-3 mb-4 flex items-start gap-2 ${clases}`}>
      <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
      <p className="text-sm">{children}</p>
    </div>
  );
}

function Datos({ children }: { children: React.ReactNode }) {
  return <dl className="space-y-2.5">{children}</dl>;
}

function Dato({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 text-sm">
      <dt className="text-sereno-dark/50 w-44 flex-shrink-0">{etiqueta}</dt>
      <dd className="flex-1 min-w-0">{children}</dd>
    </div>
  );
}

function PestanaContrato({
  ficha, sensibles, onNuevo, onEditar, onBorrar,
}: {
  ficha: FichaEmpleado;
  sensibles: DatosSensibles | null;
  onNuevo: () => void;
  onEditar: (c: Contrato) => void;
  onBorrar: (c: Contrato) => void;
}) {
  const { empleado: e } = ficha;
  const hoy = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">
          Historial de contratos
        </h3>
        <button onClick={onNuevo} className="btn-primary flex items-center gap-1.5 text-sm">
          <Plus size={16} /> Nuevo contrato
        </button>
      </div>

      {e.situacion === 'baja' && (
        <div className="card">
          <Datos>
            <Dato etiqueta="Fecha de baja">{fechaCorta(e.fechaBaja)}</Dato>
            <Dato etiqueta="Motivo">{e.motivoBaja ? MOTIVO_BAJA_LABEL[e.motivoBaja] : '—'}</Dato>
            <Dato etiqueta="Datos hasta">{fechaCorta(e.conservarHasta)}</Dato>
          </Datos>
        </div>
      )}

      {e.contratos.length === 0 ? (
        <div className="card text-center py-8">
          <p className="text-sm text-sereno-dark/40">Todavía no hay ningún contrato guardado.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {e.contratos.map((c) => {
            const vigente = c.fechaInicio <= hoy && (!c.fechaFin || c.fechaFin >= hoy);
            const vencido = !!c.fechaFin && c.fechaFin < hoy;
            const doc = e.documentos.find((d) => d.id === c.documentoId);
            return (
              <div key={c.id} className={`card ${vigente ? 'border-l-4 border-l-green-500' : ''}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium">{TIPO_CONTRATO_LABEL[c.tipo] || c.tipo}</p>
                      <span className="badge bg-gray-100 text-sereno-dark/60">
                        {JORNADA_LABEL[c.jornada]}
                        {c.horasSemana ? ` · ${c.horasSemana} h/sem` : ''}
                      </span>
                      {vigente && <span className="badge bg-green-100 text-green-700">Vigente</span>}
                      {vencido && <span className="badge bg-gray-100 text-gray-500">Terminado</span>}
                    </div>
                    <p className="text-sm text-sereno-dark/50 mt-1">
                      Del {fechaCorta(c.fechaInicio)} {c.fechaFin ? `al ${fechaCorta(c.fechaFin)}` : '— sin fecha de fin'}
                      {c.periodoPruebaHasta && ` · prueba hasta ${fechaCorta(c.periodoPruebaHasta)}`}
                    </p>
                    {c.categoria && <p className="text-sm text-sereno-dark/50">{c.categoria}</p>}
                    {c.tieneSalario && (
                      <p className="text-sm text-sereno-dark/50">
                        Salario:{' '}
                        {sensibles
                          ? `${(sensibles.salarios[c.id] ?? 0).toLocaleString('es-ES')} € brutos/año`
                          : '•••••• (se muestra en la pestaña Datos)'}
                      </p>
                    )}
                    {doc && (
                      <a
                        href={api.urlDocumentoEmpleado(e.id, doc.id)}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-sereno-dark/50 hover:text-sereno-dark mt-1"
                      >
                        <FileText size={12} /> {doc.fileName}
                      </a>
                    )}
                    {c.nota && <p className="text-sm text-sereno-dark/60 mt-1 whitespace-pre-wrap">{c.nota}</p>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => onEditar(c)} className="p-1.5 text-sereno-dark/40 hover:text-sereno-dark" title="Editar">
                      <Pencil size={15} />
                    </button>
                    <button onClick={() => onBorrar(c)} className="p-1.5 text-sereno-dark/40 hover:text-red-600" title="Borrar">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PestanaAusencias({
  ficha, onNueva, onEditar,
}: {
  ficha: FichaEmpleado;
  onNueva: () => void;
  onEditar: (a: Ausencia) => void;
}) {
  const { empleado: e, vacaciones } = ficha;
  const anio = String(vacaciones.anio);
  const delAnio = e.ausencias
    .filter((a) => a.desde.startsWith(anio) || a.hasta.startsWith(anio))
    .sort((a, b) => b.desde.localeCompare(a.desde));
  const otras = e.ausencias.filter((a) => !delAnio.includes(a)).sort((a, b) => b.desde.localeCompare(a.desde));

  const fila = (a: Ausencia) => (
    <button
      key={a.id}
      onClick={() => onEditar(a)}
      className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-sereno-blue/10 -mx-2 px-2 rounded transition-colors"
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{TIPO_AUSENCIA_LABEL[a.tipo]}</p>
        <p className="text-xs text-sereno-dark/50">
          {fechaCorta(a.desde)} — {fechaCorta(a.hasta)} · {a.diasNaturales} día{a.diasNaturales === 1 ? '' : 's'}
          {a.descuentaVacaciones && ' · descuenta vacaciones'}
        </p>
        {a.nota && <p className="text-xs text-sereno-dark/40 truncate">{a.nota}</p>}
      </div>
      <Pencil size={14} className="text-sereno-dark/30 flex-shrink-0" />
    </button>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Ausencias</h3>
          <p className="text-sm text-sereno-dark/50 mt-0.5">
            Le quedan <span className="font-semibold text-sereno-dark">{vacaciones.restantes}</span> días de
            vacaciones de {vacaciones.correspondientes} en {vacaciones.anio}
          </p>
        </div>
        <button onClick={onNueva} className="btn-primary flex items-center gap-1.5 text-sm">
          <Plus size={16} /> Nueva ausencia
        </button>
      </div>

      <div className="card">
        {delAnio.length === 0 ? (
          <p className="text-sm text-sereno-dark/40 py-2">Ninguna ausencia en {anio}.</p>
        ) : (
          <div className="divide-y divide-gray-50">{delAnio.map(fila)}</div>
        )}
      </div>

      {otras.length > 0 && (
        <div className="card">
          <h4 className="text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider mb-1">De otros años</h4>
          <div className="divide-y divide-gray-50">{otras.map(fila)}</div>
        </div>
      )}
    </div>
  );
}

function PestanaFichajes({ empleadoId }: { empleadoId: string }) {
  const [mes, setMes] = useState(mesActual());
  const { data, loading } = useApi<ResumenFichajes>(
    () => api.getFichajesEmpleado(empleadoId, mes),
    [empleadoId, mes],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">
          {nombreDelMes(mes)}
        </h3>
        <div className="flex items-center gap-1">
          <button onClick={() => setMes(desplazarMes(mes, -1))} className="btn-secondary px-2 py-1 text-sm">←</button>
          <button
            onClick={() => setMes(desplazarMes(mes, 1))}
            disabled={mes >= mesActual()}
            className="btn-secondary px-2 py-1 text-sm disabled:opacity-40"
          >
            →
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : !data ? (
        <div className="card"><p className="text-sm text-sereno-dark/40">Sin datos.</p></div>
      ) : (
        <>
          {data.aviso && <Aviso tono="amber">{data.aviso}</Aviso>}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Metrica valor={data.diasFichados} etiqueta="días fichados" />
            <Metrica valor={data.horas} etiqueta="horas" />
            <Metrica valor={data.retrasos} etiqueta={`retrasos (${data.retrasoMinutos} min)`} alerta={data.retrasos > 0} />
            <Metrica valor={data.diasSinFichar.length} etiqueta="días sin fichar" alerta={data.diasSinFichar.length > 0} />
          </div>

          {data.diasSinFichar.length > 0 && (
            <div className="card">
              <h4 className="text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider mb-2">
                Días de alta sin fichaje y sin ausencia
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {data.diasSinFichar.map((d) => (
                  <span key={d} className="badge bg-amber-100 text-amber-700">{fechaCorta(d)}</span>
                ))}
              </div>
            </div>
          )}

          {(data.dias || []).length > 0 && (
            <div className="card overflow-x-auto p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase px-4 py-2">Día</th>
                    <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase px-4 py-2">Entrada</th>
                    <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase px-4 py-2">Salida</th>
                    <th className="text-right text-xs font-semibold text-sereno-dark/50 uppercase px-4 py-2">Horas</th>
                    <th className="text-right text-xs font-semibold text-sereno-dark/50 uppercase px-4 py-2">Retraso</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.dias || []).map((d) => (
                    <tr key={d.fecha} className="border-b border-gray-50">
                      <td className="px-4 py-2">{fechaCorta(d.fecha)}</td>
                      <td className="px-4 py-2 tabular-nums">{d.entrada?.slice(11, 16) || '—'}</td>
                      <td className="px-4 py-2 tabular-nums">
                        {d.tramoAbierto ? <span className="text-amber-600">sin fichar</span> : d.salida?.slice(11, 16) || '—'}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">{d.horas || '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {d.retrasoMinutos ? <span className="text-amber-600">{d.retrasoMinutos} min</span> : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-[11px] text-sereno-dark/35">
            Los fichajes los escribe la app de housekeeping. Aquí solo se leen; para corregir uno hay que entrar allí.
          </p>
        </>
      )}
    </div>
  );
}

function Metrica({ valor, etiqueta, alerta }: { valor: number; etiqueta: string; alerta?: boolean }) {
  return (
    <div className="card">
      <p className={`text-2xl font-bold tabular-nums ${alerta ? 'text-amber-600' : ''}`}>{valor}</p>
      <p className="text-xs text-sereno-dark/50">{etiqueta}</p>
    </div>
  );
}
